import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronsUpDown,
  LogOut,
  User as UserIcon,
  Languages,
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal,
} from '../../components/ui/dropdown-menu';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '../../components/ui/sidebar';
import { safeSignOut } from '@/lib/auth-utils';
import { Skeleton } from '../../components/ui/skeleton';
import { useUsers } from '../../features/users/context/users-context';
import type { UserProfile } from '@/lib/types/core';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { changeLanguage, LANGUAGE_NAMES, type SupportedLanguage } from '../../lib/language-service';
import { toast } from '../../hooks/use-toast';
import { componentLogger } from '@/lib/logging';
import { getAttachmentsForEntity } from '@/features/attachments/operations';
import { invokeEdgeFunctionSafely } from '@/lib/supabase-functions-wrapper';

export interface NavUserProps {
  isLoading: boolean
  firstName?: string | null
  lastName?: string | null
  role?: string | null
  isRepresentative?: boolean
  onSignOut?: () => void
  userId?: string
  userProfile?: UserProfile | null
}

export function NavUser({
  isLoading,
  firstName,
  lastName,
  role,
  isRepresentative: _isRepresentative,
  onSignOut,
  userId: _userId,
  userProfile,
}: NavUserProps) {
  const { t } = useTranslation();
  const { isMobile } = useSidebar();
  const navigate = useNavigate();
  const { setDialogOpen: openUsersDialog, setCurrentUser: setSelectedUser } = useUsers();
  const [languageLoading, setLanguageLoading] = useState(false);
  const [profilePictureUrl, setProfilePictureUrl] = useState<string | null>(null);
  const [fetchingPicture, setFetchingPicture] = useState(false);

  const fullName = [firstName, lastName].filter(Boolean).join(' ') || t('userMenu.defaultUser');
  const initials = `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase() || 'U';

  const roleDisplay = role || t('userMenu.defaultRole');

  // Fetch profile picture when userProfile changes
  React.useEffect(() => {
    let mounted = true;

    const fetchProfilePicture = async () => {
      if (!userProfile?.id) {
        setProfilePictureUrl(null);
        setFetchingPicture(false);
        return;
      }

      try {
        setFetchingPicture(true);

        // Fetch attachments for this user's profile
        const attachments = await getAttachmentsForEntity({
          parentEntityId: userProfile.id,
          parentEntityType: 'PROFILE'
        });

        if (!mounted) return;

        if (attachments && attachments.length > 0) {
          const firstAttachment = attachments[0];

          if (!firstAttachment) {
            setProfilePictureUrl(null);
            return;
          }

          // Get presigned download URL
          const { data, error } = await invokeEdgeFunctionSafely<{ presignedUrl: string }>('get-download-url-for-attachment', {
            body: { attachmentId: firstAttachment.id }
          });

          if (!mounted) return;

          if (error || !data) {
            componentLogger.error('nav-user', 'Failed to fetch profile picture URL', {
              userId: userProfile.id,
              attachmentId: firstAttachment.id,
              error
            });
            setProfilePictureUrl(null);
          } else {
            setProfilePictureUrl(data.presignedUrl);
            componentLogger.debug('nav-user', 'Profile picture loaded', { userId: userProfile.id });
          }
        } else {
          setProfilePictureUrl(null);
        }
      } catch (error) {
        if (!mounted) return;
        componentLogger.error('nav-user', 'Error fetching profile picture', {
          error,
          userId: userProfile?.id
        });
        setProfilePictureUrl(null);
      } finally {
        if (mounted) {
          setFetchingPicture(false);
        }
      }
    };

    fetchProfilePicture();

    return () => {
      mounted = false;
    };
  }, [userProfile?.id]);

  const handleSignOut = async () => {
    if (onSignOut) {
      await onSignOut();
      // Add a small delay to ensure auth state is cleared
      await new Promise(resolve => setTimeout(resolve, 100));
      navigate('/login');
    } else {
      const success = await safeSignOut();
      // Add a small delay to ensure auth state is cleared
      await new Promise(resolve => setTimeout(resolve, 100));
      if (success) {
        navigate('/login');
      } else {
        // Even if signOut failed, navigate to login to be safe
        componentLogger.warn('[nav-user] Sign out may have failed, but navigating to login anyway', 'Sign out may have failed', { success });
        navigate('/login');
      }
    }
  };

  const handleLanguageChange = async (lng: string) => {
    if (languageLoading) return;
    
    setLanguageLoading(true);
    
    try {
      const result = await changeLanguage(lng as SupportedLanguage, true);
      
      if (result.success) {
        toast({
          title: t('languageSwitcher.success'),
          description: t('languageSwitcher.successMessage', { language: LANGUAGE_NAMES[lng as SupportedLanguage] }),
          variant: 'default',
        });
      } else {
        toast({
          title: t('languageSwitcher.error'),
          description: result.error || t('languageSwitcher.errorMessage'),
          variant: 'destructive',
        });
        componentLogger.warn('[nav-user] Language save failed, but UI was updated', result.error || 'Unknown error');
      }
    } catch (_error) {
      componentLogger.error('[nav-user] Failed language operation:', (_error as Error).message || 'Unknown error');
      toast({
        title: t('languageSwitcher.error'),
        description: t('languageSwitcher.errorMessage'),
        variant: 'destructive',
      });
    } finally {
      setLanguageLoading(false);
    }
  };

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size='lg'
              className='data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground w-full justify-start'
              data-testid="user-menu"
            >
              {isLoading ? (
                <div className='flex w-full items-center gap-3'>
                  <Skeleton className='h-8 w-8 rounded-full' />
                  <div className='flex flex-col items-start'>
                    <Skeleton className='h-4 w-20' />
                    <Skeleton className='h-3 w-24 mt-1' />
                  </div>
                  <ChevronsUpDown className='ml-auto size-4 text-muted-foreground' />
                </div>
              ) : (
                <div className='flex w-full items-center gap-3'>
                  <Avatar className='h-8 w-8'>
                    {!fetchingPicture && profilePictureUrl && (
                      <AvatarImage src={profilePictureUrl} alt={fullName} />
                    )}
                    <AvatarFallback>{initials}</AvatarFallback>
                  </Avatar>
                  <div className='flex flex-col items-start gap-0.5 truncate'>
                    <span className='truncate text-sm font-medium'>{fullName}</span>
                    <span className='truncate text-xs text-muted-foreground'>{roleDisplay}</span>
                  </div>
                  <ChevronsUpDown className='ml-auto size-4 text-muted-foreground' />
                </div>
              )}
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className='w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg'
            side={isMobile ? 'bottom' : 'right'}
            align='end'
            sideOffset={4}
          >
            <DropdownMenuLabel className='p-0 font-normal'>
              <div className='flex items-center gap-2 px-1 py-1.5 text-left text-sm'>
                <Avatar className='h-8 w-8 rounded-lg'>
                  {!fetchingPicture && profilePictureUrl && (
                    <AvatarImage src={profilePictureUrl} alt={fullName} />
                  )}
                  <AvatarFallback className='rounded-lg'>{initials}</AvatarFallback>
                </Avatar>
                <div className='grid flex-1 text-left text-sm leading-tight'>
                  <span className='truncate font-semibold'>{fullName}</span>
                  <span className='truncate text-xs text-muted-foreground'>{roleDisplay}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              {userProfile && (
                <DropdownMenuItem onClick={() => {
                  if (setSelectedUser) setSelectedUser(userProfile);
                  if (openUsersDialog) openUsersDialog('edit', userProfile);
                }}>
                  <UserIcon className='mr-2 size-4' />
                  {t('userMenu.profile')}
                </DropdownMenuItem>
              )}
              <DropdownMenuSub>
                <DropdownMenuSubTrigger 
                  disabled={languageLoading}
                  data-testid="language-selector"
                  aria-label="Language selector"
                >
                  <Languages className="mr-2 size-4" />
                  {languageLoading ? t('languageSwitcher.updating') : t('userMenu.language')}
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent>
                    <DropdownMenuItem 
                      onClick={() => handleLanguageChange('en')}
                      disabled={languageLoading || i18n.language === 'en'}
                      className={i18n.language === 'en' ? 'bg-accent' : ''}
                      data-testid="language-option-en"
                      data-value="en"
                    >
                      {t('languageSwitcher.english')}
                      {i18n.language === 'en' && ' ✓'}
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => handleLanguageChange('es')}
                      disabled={languageLoading || i18n.language === 'es'}
                      className={i18n.language === 'es' ? 'bg-accent' : ''}
                      data-testid="language-option-es"
                      data-value="es"
                    >
                      {t('languageSwitcher.spanish')}
                      {i18n.language === 'es' && ' ✓'}
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => handleLanguageChange('fr')}
                      disabled={languageLoading || i18n.language === 'fr'}
                      className={i18n.language === 'fr' ? 'bg-accent' : ''}
                      data-testid="language-option-fr"
                      data-value="fr"
                    >
                      {t('languageSwitcher.french')}
                      {i18n.language === 'fr' && ' ✓'}
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => handleLanguageChange('sv')}
                      disabled={languageLoading || i18n.language === 'sv'}
                      className={i18n.language === 'sv' ? 'bg-accent' : ''}
                      data-testid="language-option-sv"
                      data-value="sv"
                    >
                      {t('languageSwitcher.swedish')}
                      {i18n.language === 'sv' && ' ✓'}
                    </DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleSignOut} data-testid="logout-button">
              <LogOut className='mr-2 size-4' />
              {t('userMenu.logout')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
